import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { screensData } from '../data/productData';
import ImageModal from './ImageModal';
import '../FishFinderBuilder.css';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { fetchLowestPrice } from '../utils/api';
import FindBestPriceButton from './PriceComparison/FindBestPriceButton';

// Collapsible Important Note Component
const ImportantNote = () => {
  const [isExpanded, setIsExpanded] = useState(false); // Start collapsed for better UX

  return (
    <div style={{
      backgroundColor: 'rgba(255, 193, 7, 0.1)',
      border: isExpanded ? '2px solid #ffc107' : '1px solid #ffc107',
      borderRadius: isExpanded ? '6px' : '6px',
      padding: isExpanded ? '12px' : '8px 12px',
      marginBottom: isExpanded ? '15px' : '10px',
      position: 'relative',
      transition: 'all 0.3s ease'
    }}>
      {/* Header with warning icon and collapse/expand button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: isExpanded ? '10px' : '0',
        gap: '8px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: isExpanded ? '8px' : '6px',
          flex: 1,
          minWidth: 0 // Allow text to shrink
        }}>
          <span style={{ 
            fontSize: isExpanded ? '16px' : '16px',
            transition: 'font-size 0.3s ease',
            flexShrink: 0
          }}>⚠️</span>
          <h3 style={{
            color: '#ffc107',
            margin: 0,
            fontSize: isExpanded ? '14px' : '13px',
            fontWeight: '600',
            transition: 'font-size 0.3s ease',
            lineHeight: isExpanded ? '1.3' : '1.2',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: isExpanded ? 'normal' : 'nowrap'
          }}>
            {isExpanded ? 'Important: Garmin Networking Compatibility' : 'Important: Garmin Networking'}
          </h3>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            backgroundColor: 'rgba(255, 193, 7, 0.2)',
            border: '1px solid #ffc107',
            borderRadius: '3px',
            color: '#ffc107',
            padding: isExpanded ? '4px 8px' : '3px 6px',
            fontSize: isExpanded ? '12px' : '10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            transition: 'all 0.2s ease',
            flexShrink: 0,
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 193, 7, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 193, 7, 0.2)';
          }}
        >
          {isExpanded ? 'Hide ▲' : '▼'}
        </button>
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <div style={{
          animation: 'fadeIn 0.3s ease'
        }}>
          {/* Green checkmark section for first-time buyers */}
          <div style={{
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            border: '1px solid #4CAF50',
            borderRadius: '4px',
            padding: '8px',
            marginBottom: '10px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#4CAF50'
            }}>
              <span style={{ fontSize: '12px' }}>✓</span>
              <span style={{ fontSize: '12px', fontWeight: '500', lineHeight: '1.3' }}>
                First-time buyer? This doesn't apply to you - choose any screen you want!
              </span>
            </div>
          </div>

          {/* Main content */}
          <div style={{ color: '#ffffff', lineHeight: '1.3', fontSize: '12px' }}>
            <p style={{ margin: '0 0 8px 0' }}>
              If you already own a Garmin screen and want to add another unit that can network together (share maps, transducers, waypoints, etc.), you <strong>MUST</strong> buy a unit from the same product family.
            </p>
            <ul style={{ margin: '0', paddingLeft: '16px' }}>
              <li style={{ marginBottom: '4px' }}>
                Have an <strong>ECHOMAP?</strong> → Buy another <strong>ECHOMAP</strong> for full networking
              </li>
              <li style={{ marginBottom: '4px' }}>
                Have a <strong>GPSMAP?</strong> → Buy another <strong>GPSMAP</strong> for full networking
              </li>
              <li style={{ marginBottom: '0' }}>
                Different families (ECHOMAP + GPSMAP) can coexist but won't share data - each needs its own transducers and mapping
              </li>
            </ul>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

function QuickScreenComparison() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    screenSize: [],
    viewOnPhone: false,
    hdmiOut: false,
    hdmiIn: false,
    networkPorts: {
      one: false,
      two: false
    },
    bncVideoIn: false,
    controlMethod: []
  });
  const [modalImage, setModalImage] = useState({ isOpen: false, url: '', alt: '' });
  const [filteredScreens, setFilteredScreens] = useState(screensData);
  
  // Add state for tracking lowest prices and loading states
  const [lowestPrices, setLowestPrices] = useState({});
  const [loadingStates, setLoadingStates] = useState({}); // Track loading/retrying states
  const [globalStopRetries, setGlobalStopRetries] = useState(false); // Emergency stop for infinite retries
  
  // Add state for onboarding tooltip
  const [showOnboardingTooltip, setShowOnboardingTooltip] = useState(false);
  const filtersPanelRef = useRef(null);
  
  // Add state for show more specs glow
  const [shouldGlowShowMoreSpecs, setShouldGlowShowMoreSpecs] = useState(false);

  // Show onboarding tooltip on first visit or if forced via URL parameter
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('screenSelectionOnboardingSeen');
    const urlParams = new URLSearchParams(window.location.search);
    const forceOnboarding = urlParams.get('showOnboarding') === 'true';
    
    if (!hasSeenOnboarding || forceOnboarding) {
      // Show tooltip after a short delay to let the page load
      const timer = setTimeout(() => {
        setShowOnboardingTooltip(true);
        // Mark as seen so it doesn't show again (unless forced)
        if (!forceOnboarding) {
          localStorage.setItem('screenSelectionOnboardingSeen', 'true');
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Auto-dismiss onboarding tooltip after 15 seconds
  useEffect(() => {
    if (showOnboardingTooltip) {
      const autoDismissTimer = setTimeout(() => {
        setShowOnboardingTooltip(false);
      }, 15000);
      
      return () => clearTimeout(autoDismissTimer);
    }
  }, [showOnboardingTooltip]);

  // Check if "Show More Specs" should glow
  useEffect(() => {
    const hasUsedShowMoreSpecs = localStorage.getItem('hasUsedShowMoreSpecs');
    if (!hasUsedShowMoreSpecs) {
      setShouldGlowShowMoreSpecs(true);
    }
  }, []);

  // Function to hide onboarding tooltip when user engages with filters
  const hideOnboardingTooltip = () => {
    if (showOnboardingTooltip) {
      setTimeout(() => setShowOnboardingTooltip(false), 500);
    }
  };

  // Function to fetch lowest price for a screen
  const fetchScreenLowestPrice = async (screen, retryCount = 0) => {
    const maxRetries = 1; // Further reduced to prevent delays
    const timeoutMs = 5000; // Reduced back to 5s for faster initial load
    
    if (!screen?.csvLookupId) {
      console.log(`⚠️ [QuickScreenComparison] No csvLookupId for ${screen?.name || 'unknown screen'}`);
      return null;
    }
    
    // Emergency stop for infinite retries
    if (globalStopRetries) {
      console.log(`🚨 [QuickScreenComparison] Global stop retries activated, skipping ${screen.name}`);
      return null;
    }
    
    // Set loading state
    setLoadingStates(prev => ({
      ...prev,
      [screen.id]: retryCount === 0 ? 'loading' : `retrying-${retryCount}`
    }));
    
    try {
      console.log(`🔍 [QuickScreenComparison] Calling fetchLowestPrice for ${screen.name} (attempt ${retryCount + 1}/${maxRetries + 1}) with csvLookupId: "${screen.csvLookupId}", MSRP: $${screen.msrp}`);
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error(`Timeout after ${timeoutMs}ms`)), timeoutMs);
      });
      
      // Race between the actual API call and the timeout
      const lowestPriceData = await Promise.race([
        fetchLowestPrice(screen.csvLookupId, true, screen.msrp),
        timeoutPromise
      ]);
      
      if (lowestPriceData) {
        console.log(`✅ [QuickScreenComparison] Received pricing data for ${screen.name}:`, lowestPriceData);
        setLowestPrices(prev => ({
          ...prev,
          [screen.id]: lowestPriceData
        }));
        // Clear loading state
        setLoadingStates(prev => ({
          ...prev,
          [screen.id]: 'completed'
        }));
        return lowestPriceData;
      } else {
        console.log(`❌ [QuickScreenComparison] No pricing data returned for ${screen.name}`);
        // If no data but no error, this might be a legitimate "no data found" case
        // Set a special marker so we don't keep retrying
        setLowestPrices(prev => ({
          ...prev,
          [screen.id]: null
        }));
        // Clear loading state
        setLoadingStates(prev => ({
          ...prev,
          [screen.id]: 'no-data'
        }));
      }
    } catch (error) {
      console.error(`❌ [QuickScreenComparison] Error fetching price for ${screen.name} (attempt ${retryCount + 1}):`, error.message);
      
      // Retry if we haven't exceeded max retries AND global stop isn't activated
      if (retryCount < maxRetries && !globalStopRetries) {
        const retryDelay = 2000 + (retryCount * 1000); // 2s, 3s delays
        console.log(`🔄 [QuickScreenComparison] Retrying ${screen.name} in ${retryDelay}ms...`);
        
        // Update loading state to show retrying
        setLoadingStates(prev => ({
          ...prev,
          [screen.id]: `retrying-${retryCount + 1}`
        }));
        
        // Set a global timeout to prevent infinite retries (30 seconds max)
        if (retryCount === 0) {
          setTimeout(() => {
            console.log(`🚨 [QuickScreenComparison] Global emergency stop activated after 30 seconds`);
            setGlobalStopRetries(true);
          }, 30000);
        }
        
        await new Promise(resolve => {
          setTimeout(() => {
            resolve(fetchScreenLowestPrice(screen, retryCount + 1));
          }, retryDelay);
        });
        
        return; // Don't continue processing this attempt
      } else {
        console.error(`💀 [QuickScreenComparison] Max retries exceeded or global stop activated for ${screen.name}, giving up`);
        // Mark as failed so we don't keep trying
        setLowestPrices(prev => ({
          ...prev,
          [screen.id]: null
        }));
        // Set failed state
        setLoadingStates(prev => ({
          ...prev,
          [screen.id]: 'failed'
        }));
      }
    }
    return null;
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Effect to fetch lowest prices for screens with csvLookupId (only on mount, staggered)
  useEffect(() => {
    const screensWithPricing = screensData.filter(screen => screen.csvLookupId);
    
    if (screensWithPricing.length > 0) {
      console.log(`🔍 [QuickScreenComparison] Fetching prices for ${screensWithPricing.length} screens with pricing data:`,
        screensWithPricing.map(s => ({ id: s.id, name: s.name, csvLookupId: s.csvLookupId }))
      );
      
      // Stagger the price fetching to reduce server load and state update conflicts
      screensWithPricing.forEach((screen, index) => {
        setTimeout(() => {
          console.log(`📡 [QuickScreenComparison] Fetching price for ${screen.name} (${screen.csvLookupId})`);
          fetchScreenLowestPrice(screen);
        }, index * 200); // 200ms delay between each request
      });
    } else {
      console.log(`✅ [QuickScreenComparison] No screens with csvLookupId found`);
    }
  }, []); // Only run once on mount

  // Get the previous builder state from location
  const builderState = location.state || {};

  // Filter options
  const screenSizes = [...new Set(screensData.map(screen => screen.screenSize))].sort((a, b) => a - b);
  const controlMethods = [...new Set(screensData.map(screen => screen.controlMethod))];

  useEffect(() => {
    filterScreens();
  }, [filters]);

  const filterScreens = () => {
    let filtered = screensData.filter(screen => {
      if (filters.screenSize.length > 0 && !filters.screenSize.includes(screen.screenSize)) return false;
      if (filters.viewOnPhone && screen.viewOnPhone !== "Yes") return false;
      if (filters.hdmiOut && screen.hdmiOut !== "Yes") return false;
      if (filters.hdmiIn && screen.hdmiIn !== "Yes") return false;
      
      // Network ports filter - check if any network port filters are active
      const networkPortFiltersActive = filters.networkPorts.one || filters.networkPorts.two;
      if (networkPortFiltersActive) {
        let matchesNetworkFilter = false;
        const screenPorts = parseInt(screen.networkPorts);
        
        if (filters.networkPorts.one && screenPorts >= 1) {
          matchesNetworkFilter = true;
        }
        if (filters.networkPorts.two && screenPorts >= 2) {
          matchesNetworkFilter = true;
        }
        
        if (!matchesNetworkFilter) return false;
      }
      
      if (filters.bncVideoIn && screen.bncVideoIn !== "Yes") return false;
      
      // Control method filter - check if any control methods are selected
      if (filters.controlMethod.length > 0 && !filters.controlMethod.includes(screen.controlMethod)) return false;
      
      return true;
    });
    setFilteredScreens(filtered);
  };

  const handleScreenSizeToggle = (size) => {
    setFilters(prev => ({
      ...prev,
      screenSize: prev.screenSize.includes(size)
        ? prev.screenSize.filter(s => s !== size)
        : [...prev.screenSize, size]
    }));
    
    // Hide onboarding tooltip when user starts using filters
    if (showOnboardingTooltip) {
      setTimeout(() => setShowOnboardingTooltip(false), 500);
    }
  };

  const handleScreenSelect = (screen) => {
    setSelectedScreen(screen);
    navigate('/custom-builder', {
      state: {
        ...builderState,
        selectedScreen: screen
      }
    });
  };

  const openImageModal = (url, alt) => {
    setModalImage({ isOpen: true, url, alt });
  };

  const toggleCardExpansion = (screenId, event) => {
    // Prevent the click from triggering parent elements
    event.stopPropagation();
    
    // Mark that user has used "Show More Specs" and stop glowing
    if (shouldGlowShowMoreSpecs) {
      localStorage.setItem('hasUsedShowMoreSpecs', 'true');
      setShouldGlowShowMoreSpecs(false);
    }
    
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(screenId)) {
        newSet.delete(screenId);
      } else {
        newSet.add(screenId);
      }
      return newSet;
    });
  };

  // Helper function to render feature status
  const renderFeatureStatus = (value) => {
    if (value === "Yes") {
      return <span style={{ color: '#ffffff', fontWeight: 500, fontSize: '0.9em' }}>✓ Supported</span>;
    } else if (value === "No") {
      return <span style={{ color: '#b71c1c', fontWeight: 500, fontSize: '0.9em' }}>✗ Not Supported</span>;
    } else if (typeof value === 'number') {
      return <span style={{ color: '#ffffff', fontWeight: 500, fontSize: '0.9em' }}>{value}</span>;
    }
    return <span style={{ color: '#b71c1c', fontWeight: 500, fontSize: '0.9em' }}>✗ Not Included</span>;
  };

  // Tooltip component
  const Tooltip = ({ text }) => {
    const [visible, setVisible] = useState(false);
    const iconRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    useEffect(() => {
      if (visible && iconRef.current) {
        const rect = iconRef.current.getBoundingClientRect();
        setCoords({
          top: rect.bottom + window.scrollY + 6, // 6px gap
          left: rect.left + window.scrollX + rect.width / 2,
        });
      }
    }, [visible]);

    return (
      <>
        <span
          ref={iconRef}
          style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
          onMouseEnter={() => setVisible(true)}
          onMouseLeave={() => setVisible(false)}
          tabIndex={0}
          onFocus={() => setVisible(true)}
          onBlur={() => setVisible(false)}
        >
          <span style={{ color: '#4CAF50', marginLeft: 4, fontWeight: 'bold' }}>ⓘ</span>
        </span>
        {visible && createPortal(
          <span style={{
            minWidth: '180px',
            maxWidth: '260px',
            backgroundColor: '#222',
            color: '#fff',
            textAlign: 'left',
            borderRadius: '6px',
            padding: '8px',
            position: 'absolute',
            zIndex: 2000,
            top: coords.top,
            left: coords.left,
            transform: 'translateX(-50%)',
            opacity: 1,
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            whiteSpace: 'normal',
            pointerEvents: 'none',
          }}>
            {text}
          </span>,
          document.body
        )}
      </>
    );
  };

  // Function to toggle filters panel
  const toggleFilters = () => {
    setIsFiltersPanelOpen(!isFiltersPanelOpen);
  };

  const resetAllFilters = () => {
    setFilters({
      screenSize: [],
      viewOnPhone: false,
      hdmiOut: false,
      hdmiIn: false,
      networkPorts: {
        one: false,
        two: false
      },
      bncVideoIn: false,
      controlMethod: []
    });
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters.screenSize.length > 0 ||
           filters.viewOnPhone ||
           filters.hdmiOut ||
           filters.hdmiIn ||
           filters.networkPorts.one ||
           filters.networkPorts.two ||
           filters.bncVideoIn ||
           filters.controlMethod.length > 0;
  };

  const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 20px 0;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  `;

  const ImageContainer = styled.div`
    width: 100%;
    height: 80px;
    background-color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;

    @media (min-width: 769px) {
      height: 100px;
    }
  `;

  const ProductImage = styled.img`
    width: 85%;
    height: 85%;
    object-fit: contain;

    @media (min-width: 769px) {
      width: 95%;
      height: 95%;
    }
  `;

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: '100%',
      margin: 0,
      padding: 0,
      backgroundColor: '#000000',
      minHeight: '100vh'
    }}>
      {/* Back to Builder button - outside and above everything else */}
      <div style={{
        backgroundColor: '#000000',
        padding: '10px 0 0 0',
        paddingLeft: '24px'
      }}>
        <button
          onClick={() => navigate('/custom-builder', { state: builderState })}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '0 0 10px 0',
            width: 'fit-content'
          }}
        >
          ← Back to Builder
        </button>
      </div>

      {/* Main Content */}
      <div style={{ 
        display: 'flex',
        margin: 0,
        padding: 0,
        width: '100%',
        backgroundColor: '#000000'
      }}>
        {/* Filters Sidebar */}
        <div ref={filtersPanelRef} style={{
          width: '25%',
          backgroundColor: '#1a1a1a',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: showOnboardingTooltip ? '0 0 20px 4px rgba(52, 152, 219, 0.6), 2px 0 8px rgba(0, 0, 0, 0.2)' : '2px 0 8px rgba(0, 0, 0, 0.2)',
          animation: showOnboardingTooltip ? 'glowPulse 2s ease-in-out infinite' : 'none',
          position: 'sticky',
          top: '75px',
          height: 'calc(100vh - 75px)',
          display: 'flex',
          flexDirection: 'column',
          padding: '10px 15px',
          margin: 0,
          zIndex: 10,
          alignSelf: 'flex-start',
          transition: 'box-shadow 0.3s ease'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              margin: '0',
              color: '#ffffff',
              fontWeight: '600'
            }}>Filter Options</h3>
            {hasActiveFilters() && (
              <button
                onClick={resetAllFilters}
                style={{
                  fontSize: '11px',
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  opacity: 0.8,
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.8'}
              >
                Reset
              </button>
            )}
          </div>
          
          <div style={{
            overflowY: 'auto',
            padding: '0 0 10px 0',
            flex: 1
          }}>
            <div className="filter-section" style={{ 
              marginBottom: '8px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h4 style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                color: '#b8c7d9',
                fontWeight: '500'
              }}>Screen Size</h4>
              {screenSizes.map(size => (
                <label key={size} className="filter-checkbox" style={{
                  fontSize: '12px',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={filters.screenSize.includes(size)}
                    onChange={() => handleScreenSizeToggle(size)}
                    style={{
                      width: '12px',
                      height: '12px',
                      cursor: 'pointer'
                    }}
                  />
                  {size}″
                </label>
              ))}
            </div>

            <div className="filter-section" style={{ 
              marginBottom: '8px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h4 style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                color: '#b8c7d9',
                fontWeight: '500'
              }}>Features</h4>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                                  <input
                    type="checkbox"
                    checked={filters.viewOnPhone}
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, viewOnPhone: e.target.checked }));
                      hideOnboardingTooltip();
                    }}
                    style={{
                      width: '12px',
                      height: '12px',
                      cursor: 'pointer'
                    }}
                  />
                View on Phone
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                                  <input
                    type="checkbox"
                    checked={filters.hdmiOut}
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, hdmiOut: e.target.checked }));
                      hideOnboardingTooltip();
                    }}
                    style={{
                      width: '12px',
                      height: '12px',
                      cursor: 'pointer'
                    }}
                  />
                HDMI Out
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.hdmiIn}
                  onChange={(e) => setFilters(prev => ({ ...prev, hdmiIn: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                HDMI In
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.bncVideoIn}
                  onChange={(e) => setFilters(prev => ({ ...prev, bncVideoIn: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                BNC Video In
              </label>
            </div>

            <div className="filter-section" style={{ 
              marginBottom: '8px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h4 style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                color: '#b8c7d9',
                fontWeight: '500'
              }}>Network Ports</h4>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.networkPorts.one}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    networkPorts: { ...prev.networkPorts, one: e.target.checked }
                  }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                At least 1 port
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.networkPorts.two}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    networkPorts: { ...prev.networkPorts, two: e.target.checked }
                  }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                At least 2 ports
              </label>
            </div>

            <div className="filter-section" style={{ 
              marginBottom: '8px',
              paddingBottom: '8px'
            }}>
              <h4 style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                color: '#b8c7d9',
                fontWeight: '500'
              }}>Control Method</h4>
              {controlMethods.map(method => (
                <label key={method} className="filter-checkbox" style={{
                  fontSize: '12px',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={filters.controlMethod.includes(method)}
                    onChange={(e) => {
                      setFilters(prev => ({
                        ...prev,
                        controlMethod: e.target.checked
                          ? [...prev.controlMethod, method]
                          : prev.controlMethod.filter(m => m !== method)
                      }));
                    }}
                    style={{
                      width: '12px',
                      height: '12px',
                      cursor: 'pointer'
                    }}
                  />
                  {method}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div style={{
          width: '75%',
          padding: '0 20px 20px 20px',
          backgroundColor: '#000000',
          minHeight: '200vh'
        }}>
          <h2 style={{
            color: '#ffffff',
            fontSize: '22px',
            marginBottom: '10px',
            marginTop: '0px'
          }}>Screen Selection</h2>
          
          {/* Collapsible Important Note */}
          <ImportantNote />
          {filteredScreens.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              color: '#b8c7d9',
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              margin: '20px 0'
            }}>
              <span style={{ fontSize: '24px', marginBottom: '12px' }}>🔍</span>
              <h3 style={{ 
                margin: '0 0 8px 0',
                color: '#ffffff',
                fontSize: '18px'
              }}>No Matching Screens Found</h3>
              <p style={{ 
                margin: '0',
                fontSize: '14px',
                maxWidth: '400px',
                lineHeight: '1.5'
              }}>
                Try adjusting your filters to see more options. Consider changing the screen size or feature requirements.
              </p>
            </div>
          ) : (
            <GridContainer>
              {filteredScreens.map(screen => (
                <div key={screen.id} style={{
                  backgroundColor: '#1a1a1a',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  width: '100%'
                }}>
                  {/* White container for image */}
                  <ImageContainer onClick={() => openImageModal(screen.image, screen.name)}>
                    <ProductImage 
                      src={screen.image} 
                      alt={screen.name}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="white" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </div>
                  </ImageContainer>

                  {/* Rest of the card content */}
                  <div style={{ padding: '8px' }}>
                    <h3 style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#fff' }}>{screen.name}</h3>
                    {/* Pricing display with best price support */}
                    {(() => {
                      const screenPricing = lowestPrices[screen.id];
                      const hasLowestPrice = screenPricing && screenPricing.price;
                      const formatPrice = (price) => {
                        return typeof price === 'number' ? price.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) : price;
                      };

                      if (hasLowestPrice) {
                        return (
                          <div style={{ fontSize: '0.85em', margin: '0 0 4px 0' }}>
                            <p style={{ color: '#4CAF50', margin: '0 0 2px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                              Best Price: ${formatPrice(screenPricing.price)}
                            </p>
                            {screenPricing.msrpSavings && (
                              <div style={{
                                marginTop: '2px',
                                fontSize: '0.7em',
                                color: '#e67e22',
                                fontWeight: '500'
                              }}>
                                ↓ Save ${formatPrice(screenPricing.msrpSavings.amount)} off {screenPricing.msrpSavings.reference || 'MSRP'}
                              </div>
                            )}

                          </div>
                        );
                      } else {
                        return (
                          <div style={{ fontSize: '0.85em', margin: '0 0 4px 0' }}>
                            <p style={{ color: '#4CAF50', margin: '0 0 2px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                              MSRP: ${formatPrice(screen.msrp)}
                              <Tooltip text={'💰 This may not be the best price! Select your components, then click Find Best Price on the bundle builder page.'} />
                            </p>
                            {screen.csvLookupId && !screenPricing && (
                              <div style={{ fontSize: '0.75em', color: '#888', marginTop: '2px' }}>
                                {(() => {
                                  const loadingState = loadingStates[screen.id];
                                  if (loadingState === 'loading') {
                                    return '📡 Loading best price...';
                                  } else if (loadingState?.startsWith('retrying-')) {
                                    const retryNum = loadingState.split('-')[1];
                                    return `🔄 Retrying... (attempt ${retryNum})`;
                                  } else if (loadingState === 'failed') {
                                    return '❌ Unable to load pricing';
                                  } else if (loadingState === 'no-data') {
                                    return '💾 Best price data not available';
                                  } else {
                                    return '📡 Loading best price...';
                                  }
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      }
                    })()}
                    <div className="screen-specs" style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '0',
                      fontSize: '0.9em'
                    }}>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Screen Size:</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.screenSize}″</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Control Method:</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.controlMethod}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Resolution:</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.resolution || "Not specified"}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>View on Phone:</span>
                        {renderFeatureStatus(screen.viewOnPhone)}
                      </div>

                      {/* Expandable section */}
                      <div className={`expandable-specs ${expandedCards.has(screen.id) ? 'expanded' : ''}`} style={{ 
                        margin: 0, 
                        padding: 0,
                        borderTop: 'none',
                        marginTop: 0
                      }}>
                        {expandedCards.has(screen.id) ? (
                          <>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Avg Power Draw @ 12V:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.powerDraw12V || "Not specified"} A</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Network Ports:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.networkPorts}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>HDMI Out:</span>
                              {renderFeatureStatus(screen.hdmiOut)}
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>HDMI In:</span>
                              {renderFeatureStatus(screen.hdmiIn)}
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>BNC Video In:</span>
                              {renderFeatureStatus(screen.bncVideoIn)}
                            </div>
                          </>
                        ) : (
                          <div className="spec-row" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '4px 0',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            opacity: 0.6
                          }}>
                            <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Network Ports:</span>
                            <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.networkPorts}</span>
                          </div>
                        )}
                        
                        <button 
                          className="expand-button"
                          onClick={(e) => toggleCardExpansion(screen.id, e)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#b8c7d9',
                            width: '100%',
                            padding: '4px 0',
                            marginTop: '4px',
                            fontSize: '0.9em',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            cursor: 'pointer',
                            boxShadow: shouldGlowShowMoreSpecs && !expandedCards.has(screen.id) ? 
                              '0 0 10px 2px rgba(52, 152, 219, 0.6)' : 'none',
                            animation: shouldGlowShowMoreSpecs && !expandedCards.has(screen.id) ? 
                              'showMoreSpecsGlow 2s ease-in-out infinite' : 'none',
                            borderRadius: '4px',
                            transition: 'box-shadow 0.3s ease'
                          }}
                        >
                          {expandedCards.has(screen.id) ? (
                            <>Show Less <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▲</span></>
                          ) : (
                            <>Show More Specs <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▼</span></>
                          )}
                        </button>
                      </div>
                    </div>
                    {/* Compact Styled Action Buttons */}
                    <div style={{
                      marginTop: '8px',
                      padding: '6px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{
                        textAlign: 'center',
                        marginBottom: '6px',
                        color: '#ffffff',
                        fontSize: '0.6em',
                        fontWeight: '600',
                        opacity: 0.9,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        Choose Your Next Step:
                      </div>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {screen.csvLookupId && (
                          <div style={{ textAlign: 'center', width: '100%' }}>
                            <FindBestPriceButton
                              productId={screen.csvLookupId}
                              productType="screen"
                              text={
                                <div style={{ 
                                  textAlign: 'center', 
                                  lineHeight: '1.0',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%'
                                }}>
                                  <div style={{ lineHeight: '1.0' }}>💰 Find Best Price</div>
                                  <div style={{
                                    fontSize: '0.8em',
                                    fontWeight: '400',
                                    opacity: 0.9,
                                    marginTop: '1px',
                                    lineHeight: '1.0'
                                  }}>
                                    Compare prices across stores
                                  </div>
                                </div>
                              }
                              style={{
                                width: '100%',
                                fontSize: '0.7em',
                                padding: '6px 8px',
                                background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
                                border: 'none',
                                color: 'white',
                                borderRadius: '4px',
                                fontWeight: '600',
                                textAlign: 'center',
                                display: 'flex !important',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                                margin: '0 !important',
                                maxWidth: '100%',
                                minWidth: '0',
                                flex: 'none'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          </div>
                        )}
                        
                        <div style={{
                          textAlign: 'center',
                          fontSize: '0.55em',
                          color: '#aaa',
                          margin: '0',
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          or
                        </div>
                        
                        <button 
                          className="select-button"
                          onClick={() => handleScreenSelect(screen)}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            fontSize: '0.7em',
                            background: 'linear-gradient(135deg, #0066cc 0%, #004499 100%)',
                            border: 'none',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '2px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #0077dd 0%, #0055aa 100%)';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #0066cc 0%, #004499 100%)';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          <div style={{ lineHeight: '1.0' }}>🔧 Select & Build Setup</div>
                          <div style={{
                            fontSize: '0.85em',
                            fontWeight: '400',
                            opacity: 0.9,
                            lineHeight: '1.0'
                          }}>
                            Add transducer, battery, etc.
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </GridContainer>
          )}
        </div>
      </div>

      {/* Onboarding Tooltip */}
      {showOnboardingTooltip && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          paddingTop: '150px',
          paddingLeft: window.innerWidth <= 768 ? '20px' : '280px',
          animation: 'fadeIn 0.3s ease-out'
          }}
          onClick={(e) => {
            // Close tooltip if clicking on the overlay background
            if (e.target === e.currentTarget) {
              setShowOnboardingTooltip(false);
            }
          }}
        >
                     <div style={{
             position: 'relative',
             backgroundColor: '#ffffff',
             borderRadius: '12px',
             padding: '20px',
             maxWidth: window.innerWidth <= 768 ? '280px' : '300px',
             width: window.innerWidth <= 768 ? '90%' : 'auto',
             boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
             animation: 'slideInFromLeft 0.4s ease-out'
           }}>
            {/* Arrow pointing to filter panel */}
            {window.innerWidth > 768 && (
              <div style={{
                position: 'absolute',
                left: '-15px',
                top: '20px',
                width: 0,
                height: 0,
                borderTop: '15px solid transparent',
                borderBottom: '15px solid transparent',
                borderRight: '15px solid #ffffff'
              }} />
            )}
            
            {/* Content */}
            <div style={{ color: '#333' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <span style={{ fontSize: '20px' }}>👋</span>
                <h3 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#2c3e50'
                }}>
                  Welcome! Start Here
                </h3>
              </div>
              
              <p style={{
                margin: '0 0 16px 0',
                fontSize: '14px',
                lineHeight: '1.4',
                color: '#555'
              }}>
                Use these <strong>Filter Options</strong> to narrow down your choices based on what you want. Watch as the screen options update in real-time!
              </p>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                backgroundColor: '#e8f4f8',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#2980b9',
                fontWeight: '500',
                marginBottom: '16px'
              }}>
                <span>💡</span>
                Try selecting a screen size to get started!
              </div>
              
              <button
                onClick={() => setShowOnboardingTooltip(false)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
              >
                Got it! 👍
              </button>
            </div>
          </div>
          
          {/* CSS animations */}
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              
              @keyframes slideInFromLeft {
                from {
                  opacity: 0;
                  transform: translateX(-30px);
                }
                to {
                  opacity: 1;
                  transform: translateX(0);
                }
              }
              
              @keyframes glowPulse {
                0%, 100% {
                  box-shadow: 0 0 20px 4px rgba(52, 152, 219, 0.6), 2px 0 8px rgba(0, 0, 0, 0.2);
                }
                50% {
                  box-shadow: 0 0 30px 6px rgba(52, 152, 219, 0.8), 2px 0 8px rgba(0, 0, 0, 0.2);
                }
              }
              
              @keyframes showMoreSpecsGlow {
                0%, 100% {
                  box-shadow: 0 0 10px 2px rgba(52, 152, 219, 0.6);
                }
                50% {
                  box-shadow: 0 0 15px 3px rgba(52, 152, 219, 0.8);
                }
              }
            `}
          </style>
        </div>
      )}

      {/* Image Modal */}
      {modalImage.isOpen && (
        <ImageModal
          isOpen={modalImage.isOpen}
          imageUrl={modalImage.url}
          altText={modalImage.alt}
          onClose={() => setModalImage({ isOpen: false, url: '', alt: '' })}
        />
      )}
    </div>
  );
}

// Add these styles to your CSS file
const styles = `
@media (max-width: 768px) {
  .screen-comparison-layout {
    display: block;
  }

  .filters-sidebar {
    width: 100%;
    max-width: none;
  }

  .screens-grid {
    margin-left: 0;
  }

  .mobile-filters-button {
    display: flex !important;
  }
}

@media (min-width: 769px) {
  .mobile-filters-button {
    display: none !important;
  }
}
`;

export default QuickScreenComparison; 